import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBatchFiles, uploadFile, getExcelEditorData, saveExcelChanges } from '../services/api';
import { MaiaFile, ExcelEditorData, ExcelEditorRow } from '../types/api';
import SpreadsheetEditor, { CellChangeEvent, SpreadsheetColumn, SpreadsheetMeta } from './SpreadsheetEditor';

export default function BatchDetail() {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const [files, setFiles] = useState<MaiaFile[]>([]);
  const [editorData, setEditorData] = useState<ExcelEditorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<number | null>(null);
  const [previewFile, setPreviewFile] = useState<ExcelEditorRow | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const batchIdNum = parseInt(batchId || '0');

  useEffect(() => {
    if (!batchIdNum) return;

    loadData();
    // Poll for progress updates
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [batchIdNum]);

  const loadData = async () => {
    try {
      const [filesData, editorResult] = await Promise.all([
        getBatchFiles(batchIdNum),
        getExcelEditorData(batchIdNum)
      ]);
      setFiles(filesData);
      setEditorData(editorResult);
    } catch (error) {
      console.error('Failed to load batch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = e.target.files;
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    setUploading(true);
    try {
      for (const file of Array.from(uploadedFiles)) {
        await uploadFile(file, batchIdNum);
      }
      loadData();
    } catch (error) {
      console.error('Failed to upload files:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCellChange = async (
    row: ExcelEditorRow,
    columnIndex: number,
    oldValue: string,
    newValue: string
  ) => {
    if (!row.external_id) return;

    try {
      await saveExcelChanges({
        batchId: batchIdNum,
        file_id: row.file_id,
        external_id: row.external_id,
        cell: { x: columnIndex, y: 0 },
        oldValue,
        newValue
      });
      loadData();
    } catch (error) {
      console.error('Failed to save change:', error);
    }
  };

  const handleApprove = async (row: ExcelEditorRow, approved: boolean) => {
    if (!row.external_id) return;

    try {
      await saveExcelChanges({
        batchId: batchIdNum,
        file_id: row.file_id,
        external_id: row.external_id,
        isApproved: approved
      });
      loadData();
    } catch (error) {
      console.error('Failed to update approval:', error);
    }
  };

  const handleSpreadsheetApprove = async (rowIndex: number, approved: boolean) => {
    const filteredData = editorData?.data.filter((row) => !selectedFileId || row.file_id === selectedFileId) || [];
    const row = filteredData[rowIndex];
    if (row) {
      await handleApprove(row, approved);
    }
  };

  const handleSpreadsheetCellChange = async (event: CellChangeEvent) => {
    const filteredData = editorData?.data.filter((row) => !selectedFileId || row.file_id === selectedFileId) || [];
    const row = filteredData[event.row];
    if (!row || !row.external_id) return;

    const columnMap: Record<number, string> = {
      1: 'type',
      2: 'account_reference',
      3: 'nominal_ac_ref',
      4: 'date',
      5: 'reference',
      6: 'details',
      7: 'net_amount',
      8: 'tax_code',
      9: 'tax_amount',
    };

    const fieldName = columnMap[event.col];
    if (!fieldName) return;

    await handleCellChange(row, event.col, String(event.oldValue || ''), String(event.newValue || ''));
  };

  // Prepare spreadsheet data
  const spreadsheetData = useMemo(() => {
    if (!editorData?.data) return [];
    const filteredData = editorData.data.filter((row) => !selectedFileId || row.file_id === selectedFileId);
    return filteredData.map((row) => [
      row.approve,
      row.type,
      row.account_reference,
      row.nominal_ac_ref,
      row.date,
      row.reference,
      row.details,
      row.net_amount,
      row.tax_code,
      row.tax_amount,
    ]);
  }, [editorData?.data, selectedFileId]);

  const spreadsheetMetadata = useMemo((): SpreadsheetMeta[] => {
    if (!editorData?.data) return [];
    const filteredData = editorData.data.filter((row) => !selectedFileId || row.file_id === selectedFileId);
    return filteredData.map((row, index) => ({
      row: index,
      hasEdits: Object.keys(row.edited_fields).length > 0,
      fileId: row.file_id,
      externalId: row.external_id || undefined,
    }));
  }, [editorData?.data, selectedFileId]);

  const spreadsheetColumns: SpreadsheetColumn[] = [
    { title: 'Approve', width: 80, type: 'text', readOnly: true },
    { title: 'Type', width: 60, type: 'dropdown', source: ['PI', 'SI', 'BP', 'BR', 'JD', 'JC'] },
    { title: 'Account', width: 100, type: 'text' },
    { title: 'Nominal', width: 80, type: 'text' },
    { title: 'Date', width: 90, type: 'calendar' },
    { title: 'Reference', width: 100, type: 'text' },
    { title: 'Details', width: 200, type: 'text' },
    { title: 'Net', width: 90, type: 'numeric', align: 'right', decimal: '.' },
    { title: 'Tax Code', width: 70, type: 'dropdown', source: ['T0', 'T1', 'T2', 'T5', 'T9', 'T20'] },
    { title: 'Tax', width: 80, type: 'numeric', align: 'right', decimal: '.' },
  ];

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: 'bg-gray-100 text-gray-800',
      processing: 'bg-blue-100 text-blue-800',
      processed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      ai_analysis: 'bg-yellow-100 text-yellow-800',
      ai_success: 'bg-green-100 text-green-800',
      ai_failure: 'bg-red-100 text-red-800',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/batches')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Batch #{batchId}
              {editorData?.company && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  - {editorData.company.company_name}
                </span>
              )}
            </h1>
            <div className="text-sm text-gray-500">
              {editorData?.batch?.si_ref && <span className="mr-4">SI: {editorData.batch.si_ref}</span>}
              {editorData?.batch?.pi_ref && <span>PI: {editorData.batch.pi_ref}</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            )}
            Upload Files
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Files sidebar */}
        <div className="w-64 bg-gray-50 border-r overflow-y-auto">
          <div className="p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Files ({files.length})</h2>
            <div className="space-y-2">
              {files.map((file) => (
                <div
                  key={file.id}
                  onClick={() => setSelectedFileId(file.id === selectedFileId ? null : file.id)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedFileId === file.id
                      ? 'bg-blue-100 border-blue-300 border'
                      : 'bg-white border border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {file.name}
                      </p>
                      <div className="flex items-center mt-1 space-x-2">
                        <span className={`px-1.5 py-0.5 text-xs rounded ${getStatusBadge(file.status)}`}>
                          {file.status}
                        </span>
                        {file.progress > 0 && file.progress < 100 && (
                          <span className="text-xs text-gray-500">{file.progress}%</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {file.progress > 0 && file.progress < 100 && (
                    <div className="mt-2 w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Spreadsheet area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {spreadsheetData.length > 0 ? (
            <div className="flex-1 overflow-hidden">
              <SpreadsheetEditor
                data={spreadsheetData}
                columns={spreadsheetColumns}
                metadata={spreadsheetMetadata}
                onCellChange={handleSpreadsheetCellChange}
                onApprove={handleSpreadsheetApprove}
                height="100%"
                highlightEdited={true}
                approveColumn={0}
              />
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
              <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>No export records yet</p>
              <p className="text-sm">Upload documents to get started</p>
            </div>
          )}

          {/* Preview thumbnails for selected file */}
          {selectedFileId && editorData?.data && (
            <div className="h-24 border-t bg-gray-50 flex items-center px-4 gap-2 overflow-x-auto">
              {editorData.data
                .filter((row) => row.file_id === selectedFileId && row.preview_paths.length > 0)
                .slice(0, 1)
                .flatMap((row) => row.preview_paths)
                .map((path, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      const row = editorData.data.find((r) => r.preview_paths.includes(path));
                      if (row) setPreviewFile(row);
                    }}
                    className="h-16 w-16 flex-shrink-0 border rounded overflow-hidden hover:ring-2 hover:ring-blue-500"
                  >
                    <img src={path.startsWith('http') ? path : `/${path}`} alt={`Preview ${idx + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
            </div>
          )}
        </div>

        {/* Preview panel */}
        {previewFile && (
          <div className="w-96 bg-white border-l flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Document Preview</h3>
              <button
                onClick={() => setPreviewFile(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <p className="text-sm text-gray-600 mb-2">{previewFile.file_name}</p>
              {previewFile.preview_paths.map((path, index) => (
                <img
                  key={index}
                  src={path.startsWith('http') ? path : `/${path}`}
                  alt={`Page ${index + 1}`}
                  className="w-full mb-4 border rounded shadow-sm"
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
